<script setup>
import { computed, onMounted, ref, watch, nextTick } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';

const props = defineProps({
  assistantId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['close']);
const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const rebindDialogRef = ref(null);
const i18nKey = 'PILOT.INBOXES.CREATE';

const formState = {
  uiFlags: useMapGetter('pilotInboxes/getUIFlags'),
  availableInboxes: useMapGetter('pilotAvailableInboxes/getRecords'),
  linkedInboxes: useMapGetter('pilotInboxes/getRecords'),
};

const state = ref({ inboxId: null, isProcessing: false });
const searchQuery = ref('');
const showRebindConfirm = ref(false);
const pendingInbox = ref(null);

// 批量选择状态
const batchSelectedIds = ref(new Set());
const isBatchSubmitting = ref(false);
const hoveredInboxId = ref(null);

// 监听 showRebindConfirm，使用 showModal() 打开弹窗
watch(showRebindConfirm, async newVal => {
  await nextTick();
  if (newVal && rebindDialogRef.value) {
    rebindDialogRef.value.showModal();
  } else if (!newVal && rebindDialogRef.value) {
    rebindDialogRef.value.close();
  }
});

// 获取收件箱类型标签
const getChannelTypeLabel = inbox => {
  const typeMap = {
    'Channel::WebWidget': '网站',
    'Channel::WeChat': '微信',
    'Channel::Email': '邮箱',
    'Channel::Whatsapp': 'WhatsApp',
    'Channel::FacebookPage': 'Facebook',
    'Channel::TwitterProfile': 'Twitter',
    'Channel::Telegram': 'Telegram',
    'Channel::Line': 'Line',
    'Channel::Sms': '短信',
    'Channel::Api': 'API',
  };
  return (
    typeMap[inbox.channel_type] ||
    inbox.channel_type?.replace('Channel::', '') ||
    '未知'
  );
};

// 当前助手已绑定的收件箱 ID 集合
const linkedInboxIds = computed(() => {
  const list = formState.linkedInboxes.value || [];
  return new Set(list.map(inbox => inbox.id));
});

// 判断收件箱是否已被当前助手绑定
const isLinkedInbox = inbox => linkedInboxIds.value.has(inbox.id);

// 判断收件箱是否已被其他助手占用
const isOccupiedByOther = inbox =>
  inbox.linked_assistant && !isLinkedInbox(inbox);

// 根据搜索过滤
const filteredInboxes = computed(() => {
  const query = searchQuery.value.toLowerCase().trim();
  const list = formState.availableInboxes.value || [];
  if (!query) return list;
  return list.filter(
    inbox =>
      inbox.name?.toLowerCase().includes(query) ||
      inbox.linked_assistant?.name?.toLowerCase().includes(query)
  );
});

const isLoading = computed(
  () => formState.uiFlags.value.creatingItem || state.value.isProcessing
);

// 批量选择相关
const isSelected = inboxId => batchSelectedIds.value.has(inboxId);

const toggleSelection = inboxId => {
  if (batchSelectedIds.value.has(inboxId)) {
    batchSelectedIds.value.delete(inboxId);
  } else {
    batchSelectedIds.value.add(inboxId);
  }
  batchSelectedIds.value = new Set(batchSelectedIds.value);
};

const hasSelection = computed(() => batchSelectedIds.value.size > 0);

// 全选/取消全选标签
const buildSelectedCountLabel = computed(() => {
  const count = filteredInboxes.value.length;
  const isAllSelected = batchSelectedIds.value.size === count && count > 0;
  return isAllSelected ? `取消全选 (${count})` : `全选 (${count})`;
});

const selectedCountLabel = computed(() => {
  return `已选择 ${batchSelectedIds.value.size} 项`;
});

// 全选/取消全选
const toggleSelectAll = () => {
  const allItems = filteredInboxes.value;
  const allSelected =
    batchSelectedIds.value.size === allItems.length && allItems.length > 0;

  if (allSelected) {
    batchSelectedIds.value = new Set();
  } else {
    batchSelectedIds.value = new Set(allItems.map(inbox => inbox.id));
  }
};

// 连接收件箱
const handleConnect = async inbox => {
  if (isOccupiedByOther(inbox)) {
    pendingInbox.value = inbox;
    showRebindConfirm.value = true;
    return;
  }

  state.value.inboxId = inbox.id;
  state.value.isProcessing = true;
  try {
    await store.dispatch('pilotInboxes/create', {
      inboxId: inbox.id,
      assistantId: props.assistantId,
    });
    await refreshData();
    useAlert('收件箱已成功连接');
  } catch (error) {
    useAlert(error?.message || '连接失败，请重试');
  } finally {
    state.value.isProcessing = false;
    state.value.inboxId = null;
  }
};

// 确认重新绑定
const handleConfirmRebind = async () => {
  if (!pendingInbox.value) return;

  const inbox = pendingInbox.value;
  showRebindConfirm.value = false;
  state.value.inboxId = inbox.id;
  state.value.isProcessing = true;

  try {
    await store.dispatch('pilotInboxes/delete', {
      assistantId: inbox.linked_assistant?.id,
      inboxId: inbox.id,
    });
    await store.dispatch('pilotInboxes/create', {
      assistantId: props.assistantId,
      inboxId: inbox.id,
    });
    await refreshData();
    useAlert('收件箱已重新绑定');
  } catch (error) {
    useAlert(error?.message || '重新绑定失败，请重试');
  } finally {
    state.value.isProcessing = false;
    state.value.inboxId = null;
    pendingInbox.value = null;
  }
};

// 取消重新绑定
const handleCancelRebind = () => {
  showRebindConfirm.value = false;
  pendingInbox.value = null;
};

// 断开连接
const handleDisconnect = async inbox => {
  state.value.inboxId = inbox.id;
  state.value.isProcessing = true;
  try {
    await store.dispatch('pilotInboxes/delete', {
      inboxId: inbox.id,
      assistantId: props.assistantId,
    });
    await refreshData();
    useAlert('收件箱已断开连接');
  } catch (error) {
    useAlert(error?.message || '断开连接失败，请重试');
  } finally {
    state.value.isProcessing = false;
    state.value.inboxId = null;
  }
};

// 批量操作
const handleBatchOperation = async action => {
  if (!hasSelection.value || isBatchSubmitting.value) return;

  isBatchSubmitting.value = true;
  try {
    let ids = Array.from(batchSelectedIds.value);
    const inboxesToProcess = filteredInboxes.value.filter(inbox =>
      ids.includes(inbox.id)
    );

    // 批量连接时，过滤掉已关联的和被其他助手占用的
    if (action === 'connect') {
      const toConnect = inboxesToProcess.filter(
        inbox => !isLinkedInbox(inbox) && !isOccupiedByOther(inbox)
      );
      for (const inbox of toConnect) {
        await store.dispatch('pilotInboxes/create', {
          inboxId: inbox.id,
          assistantId: props.assistantId,
        });
      }
      useAlert(
        toConnect.length > 0
          ? `已批量连接 ${toConnect.length} 个收件箱`
          : '没有可连接的收件箱'
      );
    }

    // 批量断开时，只处理已关联的
    if (action === 'disconnect') {
      const toDisconnect = inboxesToProcess.filter(inbox =>
        isLinkedInbox(inbox)
      );
      for (const inbox of toDisconnect) {
        await store.dispatch('pilotInboxes/delete', {
          inboxId: inbox.id,
          assistantId: props.assistantId,
        });
      }
      useAlert(
        toDisconnect.length > 0
          ? `已批量断开 ${toDisconnect.length} 个收件箱`
          : '没有可断开的收件箱'
      );
    }

    await refreshData();
    batchSelectedIds.value = new Set();
  } catch (error) {
    useAlert(error?.message || '批量操作失败，请重试');
  } finally {
    isBatchSubmitting.value = false;
  }
};

// 刷新数据
const refreshData = async () => {
  await store.dispatch('pilotInboxes/get', { assistantId: props.assistantId });
  await store.dispatch('pilotAvailableInboxes/get');
};

// 加载数据
const fetchData = async () => {
  await store.dispatch('pilotAvailableInboxes/get');
  await store.dispatch('pilotInboxes/get', { assistantId: props.assistantId });
};

const handleClose = () => {
  emit('close');
};

const handleCancel = () => {
  dialogRef.value?.close();
};

// 打开弹窗
const open = async () => {
  searchQuery.value = '';
  batchSelectedIds.value = new Set();
  dialogRef.value?.open();
  await fetchData();
};

onMounted(() => {
  fetchData();
});

defineExpose({ dialogRef, open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="$t(`${i18nKey}.TITLE`)"
    :show-cancel-button="false"
    :show-confirm-button="false"
    width="xl"
    overflow-y-auto
    @close="handleClose"
  >
    <div class="flex flex-col gap-4">
      <!-- 搜索工具条 - 无选中时显示 -->
      <div v-if="!hasSelection" class="flex items-center gap-2">
        <Input
          v-model="searchQuery"
          type="search"
          size="sm"
          :placeholder="
            $t(
              'PILOT.ASSISTANTS.CONFIG.KNOWLEDGE_BASES.SELECTOR.SEARCH_PLACEHOLDER'
            )
          "
          :custom-input-class="[
            'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8',
          ]"
          class="flex-1"
        >
          <template #prefix>
            <Icon
              icon="i-lucide-search"
              class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
            />
          </template>
        </Input>
      </div>

      <!-- 批量操作工具条 - 有选中时显示 -->
      <div v-else class="flex items-center gap-3 px-3">
        <div class="flex items-center gap-2">
          <Checkbox
            :model-value="
              batchSelectedIds.size === filteredInboxes.length &&
              filteredInboxes.length > 0
            "
            :indeterminate="
              batchSelectedIds.size > 0 &&
              batchSelectedIds.size < filteredInboxes.length
            "
            @update:model-value="toggleSelectAll"
          />
          <span class="text-sm font-medium text-n-slate-12 tabular-nums">
            {{ buildSelectedCountLabel }}
          </span>
        </div>
        <span class="text-sm text-n-slate-10 truncate tabular-nums">
          {{ selectedCountLabel }}
        </span>
        <div class="h-4 w-px bg-n-strong" />
        <Button
          label="批量连接"
          size="sm"
          variant="ghost"
          color="slate"
          :is-loading="isBatchSubmitting"
          @click="handleBatchOperation('connect')"
        />
        <Button
          label="批量断开"
          size="sm"
          variant="ghost"
          color="ruby"
          :is-loading="isBatchSubmitting"
          @click="handleBatchOperation('disconnect')"
        />
      </div>

      <!-- 收件箱列表 -->
      <div class="flex flex-col gap-2 max-h-96 overflow-y-auto">
        <!-- 空状态 -->
        <div
          v-if="filteredInboxes.length === 0"
          class="py-8 text-center text-sm text-n-slate-11"
        >
          {{
            $t('PILOT.ASSISTANTS.CONFIG.KNOWLEDGE_BASES.SELECTOR.EMPTY_SEARCH')
          }}
        </div>

        <!-- 列表 -->
        <template v-else>
          <div
            v-for="inbox in filteredInboxes"
            :key="inbox.id"
            class="flex items-center gap-3 p-3 rounded-lg border transition-colors"
            :class="[
              isSelected(inbox.id)
                ? 'border-n-brand bg-n-brand-50 dark:bg-n-brand-500/10'
                : 'border-n-slate-6 hover:border-n-slate-8 hover:bg-n-slate-1 dark:hover:bg-n-slate-800',
            ]"
            @mouseenter="hoveredInboxId = inbox.id"
            @mouseleave="hoveredInboxId = null"
          >
            <!-- Checkbox - hover或有选中时才显示 -->
            <div
              :class="{
                invisible:
                  hoveredInboxId !== inbox.id && batchSelectedIds.size === 0,
              }"
            >
              <Checkbox
                :model-value="isSelected(inbox.id)"
                @update:model-value="toggleSelection(inbox.id)"
                @click.stop
              />
            </div>

            <!-- 图标/头像 -->
            <div
              class="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden bg-n-slate-3 dark:bg-n-slate-700"
            >
              <img
                v-if="inbox.avatar_url"
                :src="inbox.avatar_url"
                :alt="inbox.name"
                class="w-full h-full object-cover"
              />
              <ChannelIcon v-else :inbox="inbox" class="size-4" />
            </div>

            <!-- 名称和类型 -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <span class="text-sm text-n-slate-12 truncate">{{
                  inbox.name
                }}</span>
                <span
                  v-if="isLinkedInbox(inbox)"
                  class="px-1.5 py-0.5 text-xs font-medium text-white whitespace-nowrap rounded bg-n-ruby-9"
                >
                  已关联
                </span>
              </div>
              <div class="flex items-center gap-2 mt-0.5">
                <span class="text-xs text-n-slate-10">{{
                  getChannelTypeLabel(inbox)
                }}</span>
                <span
                  v-if="isOccupiedByOther(inbox)"
                  class="text-xs text-n-slate-9"
                >
                  · 被「{{ inbox.linked_assistant?.name }}」占用
                </span>
              </div>
            </div>

            <!-- 操作按钮 -->
            <Button
              v-if="isLinkedInbox(inbox)"
              icon="i-lucide-minus"
              size="sm"
              variant="faded"
              color="ruby"
              :is-loading="isLoading && state.inboxId === inbox.id"
              :disabled="isLoading"
              @click="handleDisconnect(inbox)"
            />
            <Button
              v-else
              icon="i-lucide-plus"
              size="sm"
              variant="faded"
              color="slate"
              :is-loading="isLoading && state.inboxId === inbox.id"
              :disabled="isLoading"
              @click="handleConnect(inbox)"
            />
          </div>
        </template>
      </div>

      <!-- 底部按钮 -->
      <div
        class="flex items-center justify-end gap-2 pt-4 border-t border-n-slate-6"
      >
        <Button
          :label="$t('PILOT.ASSISTANTS.CONFIG.KNOWLEDGE_BASES.SELECTOR.CLOSE')"
          variant="faded"
          color="slate"
          @click="handleCancel"
        />
      </div>
    </div>
  </Dialog>

  <!-- 重新绑定确认弹窗 - 使用原生 dialog 元素 -->
  <dialog
    ref="rebindDialogRef"
    class="w-full max-w-md p-0 rounded-xl shadow-xl bg-n-solid-1 dark:bg-n-solid-2"
    style="border: none"
  >
    <div class="p-6">
      <h3 class="text-base font-medium text-n-slate-12 mb-2">重新绑定收件箱</h3>
      <p class="text-sm text-n-slate-11 mb-6">
        该收件箱已被「{{
          pendingInbox?.linked_assistant?.name || '其他助手'
        }}」占用，是否将其重新绑定到当前助手？
      </p>
      <div class="flex gap-3">
        <Button
          variant="faded"
          color="slate"
          label="取消"
          class="flex-1"
          :disabled="state.isProcessing"
          @click="handleCancelRebind"
        />
        <Button
          color="ruby"
          label="确认重新绑定"
          class="flex-1"
          :is-loading="state.isProcessing"
          :disabled="state.isProcessing"
          @click="handleConfirmRebind"
        />
      </div>
    </div>
  </dialog>
</template>

<style scoped>
dialog::backdrop {
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}
</style>
